<script lang="ts">
    import { computed } from 'vue';
    import { JourneyNode } from '@journeys/Journeys.viewmodel';
    import { NodeType, NodeConditionVariation, NodeTaskVariation } from '@journeys/Enums';
    import NodeTypeSegment from '../Partials/NodeTypeSegment.vue';
    import NodeTypeTask from '../Partials/NodeTypeTask.vue';
    import NodeTypeTag from '../Partials/NodeTypeTag.vue';
    import NodeTypeContactList from '../Partials/NodeTypeContactList.vue';
    import NodeDelayFields from '../Partials/NodeDelayFields.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormTextArea from '@shared/components/Form/FormTextArea.vue';
    import vm from '@journeys/Journeys.viewmodel';
    import BsAlert from '@cwd/Shared/components/BsAlert.vue';
    import { AlertTypes } from '@cwd/Shared/components/BsAlerts';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'Tab_NodeSettings_Data',
    };
    export default {
        name: component.name,
        components: { NodeTypeSegment, BsAlert },
    };
</script>

<script lang="ts" setup>
    const props = defineProps<{
        node: JourneyNode;
    }>();

    let nodeTypes: { [key: string]: { id: number; name: string } } = {};

    for (var type in NodeType) {
        if (typeof NodeType[type] === 'number') {
            nodeTypes[NodeType[type]] = {
                id: parseInt(NodeType[type]),
                name: type,
            };
        }
    }

    const typeMenuComponent = computed(() => {
        switch (NodeType[props.node.data.type]) {
            case 'Segment':
                return NodeTypeSegment;
            case 'Task':
                if (props.node.data.var === NodeTaskVariation.Tag) {
                    return NodeTypeTag;
                }
                if (props.node.data.var === NodeTaskVariation.ContactList) {
                    return NodeTypeContactList;
                }
                return NodeTypeTask;
            default:
                return;
        }
    });

    const hasDuplicateTitle = computed(() => {
        const currentNode = props.node;
        const currentTitle = currentNode.data.title?.trim();

        // Only check for email and SMS nodes
        const isEmailOrSms =
            currentNode.data.type === NodeType.Task && (currentNode.data.var === NodeTaskVariation.Email || currentNode.data.var === NodeTaskVariation.Sms);

        if (!isEmailOrSms || !currentTitle) {
            return false;
        }

        // Check all nodes in the journey
        const allNodes = Object.values(vm.activeJourney.nodes) as JourneyNode[];
        const duplicateNodes = allNodes.filter((node: JourneyNode) => {
            // Skip the current node
            if (node.id === currentNode.id) {
                return false;
            }

            // Only check email and SMS nodes
            const isOtherEmailOrSms =
                node.data.type === NodeType.Task && (node.data.var === NodeTaskVariation.Email || node.data.var === NodeTaskVariation.Sms);

            if (!isOtherEmailOrSms) {
                return false;
            }

            // Check if title matches (case-insensitive)
            return node.data.title?.trim().toLowerCase() === currentTitle.toLowerCase();
        });

        return duplicateNodes.length > 0;
    });
</script>

<template>
    <div class="form-group">
        <FormInput
            :class="{ 'has-warning': hasDuplicateTitle }"
            v-model="node.data.title"
            :label="$Resources.TitleLabel"
            :placeholder="$Resources.TitleLabel"></FormInput>

        <BsAlert
            class="m-sm-t"
            v-if="hasDuplicateTitle"
            :dismissible="false"
            :type="AlertTypes.Warning"
            :title="$Resources.JourneyDuplicateTitleWarningTitle"
            :content="$Resources.JourneyDuplicateTitleWarningContent" />
    </div>

    <FormTextArea
        class="form-group"
        v-model="node.data.description"
        :label="$Resources.DescriptionLabel"
        :placeholder="$Resources.DescriptionLabel"
        :rows="2"></FormTextArea>

    <template v-if="node.data.type === NodeType.Condition && node.data.var === NodeConditionVariation.Delay">
        <NodeDelayFields :node="node" />
    </template>

    <component :is="typeMenuComponent" :data="node.data" :id="node.id"></component>
</template>
